export const GTMEventType = {
  PAGE_VIEW: 'we_page_load',
  SECTION_VIEW: 'gtm_section_view',
  CLICK: 'gtm_custom_click',
  HOVER: 'hover',
  FORM_SUBMIT_STATUS: 'form_submit_status',
  FORM_INPUT: 'career_profile_evaluation_form_input_click',
  CTA_CLICK: 'career_profile_evaluation_cta_click',
  SET_USER_ATTRIBUTE: 'set_user_attribute'
};

// Scaler-standard identifiers for the career-profile-evaluation tool.
// Mirror of the schema deployed on AI Fluency Assessment — only the
// product/subproduct identifiers differ.
export const PRODUCT_ID = 'career_profile_evaluation';
export const SUB_PRODUCT_ID = 'lp-business-and-ai-readiness';
export const PIXEL_ID = 'opgp';
export const PROGRAM_ID = 'opgp';
export const COMPANY_ID = 'scaler';
export const REGION_ID = 'india';
export const FORM_TYPE = 'form_first';

function _readCookie(name) {
  if (typeof document === 'undefined') return '';
  const m = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\/+^])/g, '\\$1') + '=([^;]*)')
  );
  return m ? decodeURIComponent(m[1]) : '';
}
function _googleClientId() {
  const ga = _readCookie('_ga');
  if (!ga) return '';
  const parts = ga.split('.');
  return parts.length >= 4 ? parts.slice(-2).join('.') : ga;
}
function _device() {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return 'mobile';
  if (/iPad|Tablet/i.test(ua)) return 'tablet';
  return 'desktop';
}
function _isoDate() { return new Date().toISOString(); }
function _tzOffset() { return new Date().getTimezoneOffset(); }
function _isIframe() {
  try { return window.self !== window.top; } catch (_) { return true; }
}
function _utmPropagation() {
  try {
    const sp = new URL(window.location.href).searchParams;
    const keys = ['utm_source','utm_medium','utm_campaign','utm_term','utm_content','gclid','fbclid'];
    const out = {};
    keys.forEach((k) => { const v = sp.get(k); if (v) out[k] = v; });
    return out;
  } catch (_) { return {}; }
}
function _visitDetails() {
  try {
    const KEY = 'cpe_lp_visit_details';
    const raw = localStorage.getItem(KEY);
    const now = new Date().toISOString();
    let v = raw ? JSON.parse(raw) : { visit_count: 0, first_visit_at: now, last_visit_at: null };
    v.visit_count = (v.visit_count || 0) + 1;
    v.last_visit_at = now;
    localStorage.setItem(KEY, JSON.stringify(v));
    return v;
  } catch (_) {
    return { visit_count: 1, first_visit_at: _isoDate(), last_visit_at: _isoDate() };
  }
}
// Identifies which Scaler tool the user came from, parsed from referrer path.
// e.g. "/ai-business-fluency-assessment/" -> "ai_fluency_assessment".
function _sourceTool() {
  try {
    if (!document.referrer) return '';
    const u = new URL(document.referrer);
    if (!/scaler\.com$/i.test(u.hostname) && u.hostname !== 'www.scaler.com') return '';
    const p = u.pathname.toLowerCase();
    if (p.includes('ai-business-fluency-assessment')) return 'ai_fluency_assessment';
    if (p.includes('career-profile-evaluation') || p.includes('career-profile-tool')) return 'career_profile_evaluation';
    if (p.includes('ai-readiness')) return 'ai_readiness_assessment';
    if (p.includes('pgpmt') || p.includes('opgp')) return 'opgp_landing';
    const seg = p.split('/').filter(Boolean)[0];
    return seg ? seg.replace(/-/g, '_') : '';
  } catch (_) { return ''; }
}
export function buildStandardAttrs({ subproduct = SUB_PRODUCT_ID, extra = {} } = {}) {
  const url = (typeof window !== 'undefined') ? new URL(window.location.href) : null;
  return {
    pixel: PIXEL_ID,
    program: PROGRAM_ID,
    product: PRODUCT_ID,
    subproduct,
    company: COMPANY_ID,
    region: REGION_ID,
    request_source: 'web',
    isFreeProductUser: true,
    form_type: FORM_TYPE,
    device: _device(),
    iframe: _isIframe(),
    cu_date: _isoDate(),
    cu_timezone_offset: _tzOffset(),
    google_client_id: _googleClientId(),
    utm_propagation_params: _utmPropagation(),
    experiments: '',
    ab_experiments: {},
    'referrer-url': (typeof document !== 'undefined' ? document.referrer : '') || '',
    'referrer-host': (function () {
      try { return document.referrer ? new URL(document.referrer).host : ''; } catch (_) { return ''; }
    })(),
    source_tool: _sourceTool(),
    visit_details: _visitDetails(),
    page_title: (typeof document !== 'undefined') ? document.title : '',
    page_path: url ? url.pathname : '',
    page_url: url ? url.href : '',
    query_params: url ? Object.fromEntries(url.searchParams.entries()) : {},
    ...extra
  };
}

function isUndefined(v) {
  return typeof v === 'undefined';
}

function isObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function deepMerge(target, source) {
  const out = Array.isArray(target) ? { ...target } : { ...target };
  Object.keys(source || {}).forEach((key) => {
    const sVal = source[key];
    const tVal = out[key];
    if (isObject(tVal) && isObject(sVal)) {
      out[key] = deepMerge(tVal, sVal);
    } else {
      out[key] = sVal;
    }
  });
  return out;
}

function removeEmptyKeys(obj) {
  if (!isObject(obj)) return obj;
  const out = {};
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (!isUndefined(v) && v !== null && v !== '') {
      out[k] = isObject(v) ? removeEmptyKeys(v) : v;
    }
  });
  return out;
}

const DEFAULT_CONFIG = {
  isEnabled: true,
  attributes: {}
};

class Tracker {
  constructor(config = {}) {
    const finalConfig = { ...DEFAULT_CONFIG, ...config };
    this._platform = 'web';
    this._isLoggedIn = false;
    this._isEnabled = Boolean(finalConfig.isEnabled);
    this._shouldTrack = true;
    this._superAttributes = finalConfig.attributes || {};
    this._pendingList = [];
    this._pushToPendingList = false;

    this._initialiseDataLayer();
  }

  _isWindowUndefined() {
    return typeof window === 'undefined';
  }

  _initialiseDataLayer() {
    if (this._isWindowUndefined()) return;
    window.dataLayer = window.dataLayer || [];
  }

  get isEnabled() {
    return this._isEnabled;
  }

  get shouldTrack() {
    return this._shouldTrack;
  }

  get superAttributes() {
    return this._superAttributes;
  }

  set superAttributes(attributes) {
    this._superAttributes = deepMerge(this.superAttributes, attributes);
  }

  set isLoggedIn(status) {
    this._isLoggedIn = status;
  }

  set pushToPendingList(shouldPush) {
    this._pushToPendingList = shouldPush;
    if (!shouldPush) {
      this._pushPendingEvents();
    }
  }

  _pushPendingEvents() {
    if (this._pendingList.length) {
      this._pendingList.forEach(({ event, attributes }) =>
        this._trackEvent(event, attributes)
      );
      this._pendingList = [];
    }
  }

  _createEventPayload(event, _attributes) {
    const { custom: customAttributes, ...attributes } = _attributes;
    const {
      custom: customSuperAttributes = {},
      attributes: superAttributes = {}
    } = this.superAttributes;

    return {
      event,
      attributes: {
        is_logged_in: this._isLoggedIn,
        ...superAttributes,
        ...attributes
      },
      custom_attributes: {
        ...customSuperAttributes,
        ...(customAttributes || {})
      }
    };
  }

  _pushToDataLayer(payload) {
    window.dataLayer.push({
      _clear: true,
      ...payload
    });
  }

  _logEvent(payload) {
    console.info(
      `"${payload.event}" event has been received with below payload: `,
      payload
    );
  }

  sectionView(attributes) {
    this._trackEvent(GTMEventType.SECTION_VIEW, attributes);
  }

  _trackEvent(event, _attributes) {
    if (!this.isEnabled) return;
    if (this._pushToPendingList) {
      this._pendingList.push({ event: String(event), attributes: _attributes });
      return;
    }

    const attributes = removeEmptyKeys(_attributes);
    const eventPayload = this._createEventPayload(String(event), attributes);

    if (this.shouldTrack) {
      this._pushToDataLayer(eventPayload);
    } else {
      this._logEvent(eventPayload);
    }
  }

  _createPageViewAttributes(attributes) {
    const { title } = window.document;
    const url = new URL(window.location.href);
    return {
      page_title: title,
      page_path: url.pathname,
      page_url: url.href,
      query_params: Object.fromEntries(url.searchParams),
      ...attributes
    };
  }

  pushRawEvent(event) {
    this._pushToDataLayer(event);
  }

  click(attributes) {
    this._trackEvent(GTMEventType.CLICK, attributes);
  }

  pageview(attributes = {}) {
    const subproduct = attributes.subproduct || SUB_PRODUCT_ID;
    const std = buildStandardAttrs({ subproduct });
    // Promote the full schema onto superAttributes so every subsequent event
    // (clicks, section views, form submits) inherits the same context.
    this.superAttributes = { attributes: std };
    const action = subproduct === 'thank_you_page' ? 'thank_you_page' : 'details_page';
    this._trackEvent(GTMEventType.PAGE_VIEW, { action, ...std, ...attributes });
  }

  thankYouPageview(attributes = {}) {
    this.pageview({ subproduct: 'thank_you_page', ...attributes });
  }

  setUserAttribute(userAttributes = {}) {
    if (!this.isEnabled || this._isWindowUndefined()) return;
    const filtered = {};
    Object.keys(userAttributes || {}).forEach((k) => {
      const v = userAttributes[k];
      if (v !== null && v !== undefined) filtered[k] = v;
    });
    const { attributes: superAttrs = {} } = this.superAttributes || {};
    const payload = {
      event: GTMEventType.SET_USER_ATTRIBUTE,
      action: filtered.email || filtered.action || GTMEventType.SET_USER_ATTRIBUTE,
      user_attributes: filtered,
      attributes: { ...superAttrs, ...filtered }
    };
    window.dataLayer.push(payload);
  }

  // Scaler-standard mapping from raw lead fields (name/email/phone) to the
  // we_* + cu_* user attribute keys defined in the Data Layer sheet.
  identifyLead({ name, email, phone, role } = {}) {
    this.isLoggedIn = true;
    const firstName = (name || '').trim().split(/\s+/)[0] || '';
    this.setUserAttribute({
      email: email || '',
      we_email: email || '',
      we_phone: phone || '',
      we_first_name: firstName,
      we_whatsapp_opt_in: false,
      cu_full_name: name || '',
      cu_user_type: 'mentee',
      cu_free_mentee: true,
      cu_is_parent: '',
      cu_is_ug_enrolled: false,
      cu_twelfth_graduation_year: '',
      cu_year_of_graduation: '',
      cu_months_of_experience: '',
      mentee_is_paid: false,
      role: role || '',
      pixel: PIXEL_ID,
      program: PROGRAM_ID,
      product: PRODUCT_ID,
      subproduct: SUB_PRODUCT_ID,
      company: COMPANY_ID,
      region: REGION_ID
    });
  }

  hover(attributes) {
    this._trackEvent(GTMEventType.HOVER, attributes);
  }

  formSubmitStatus(attributes) {
    this._trackEvent(GTMEventType.FORM_SUBMIT_STATUS, attributes);
  }

  formInput(attributes) {
    this._trackEvent(GTMEventType.FORM_INPUT, attributes);
  }

  ctaClick(attributes) {
    this._trackEvent(GTMEventType.CTA_CLICK, attributes);
  }
}

const tracker = new Tracker({});
export default tracker;
